/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import axios from 'axios'
axios.defaults.headers.common['Accept'] = 'application/json'

/**
 * A creation must never carry an id: newForm is a long-lived store object that
 * still holds the last edited form, and a stale id makes the backend graft the
 * new steps onto that form instead of creating fresh ones.
 */
function createPayload(form) {
    const payload = { ...form }
    delete payload.id
    return payload
}

/**
 * Forms don't need pagination, they should always be less than 10
 * @param context
 * @returns {Promise<unknown>}
 */

export default {
    /*
    |--------------------------------------------------------------------------
    | GET form
    |--------------------------------------------------------------------------
    |
    */
    async getForms({ rootState, commit, dispatch, state }) {
        let params = await dispatch('prepareParams', state.formsConfig, {
            root: true
        })

        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/forms`, { params })
                .then((response) => {
                    commit('setForms', response.data.forms)
                    commit('setFormsTypes', response.data.types)

                    resolve()
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getForm({ rootState, state, commit }, id) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/forms/${id}`, {
                    params: { include: 'statuses.actions' }
                })
                .then((response) => {
                    state.form = null
                    commit('setForm', response.data.form)
                    resolve(response.data.form)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getPublicForm({ rootState, state, commit }, id) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/forms/${id}/public`)
                .then((response) => {
                    commit('setForm', response.data.form)
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    initForm({ state, commit }) {
        // Deep copy: committing defaultForm itself aliases newForm onto it, so
        // every write the builder makes pollutes the default reused next time.
        commit('setNewForm', JSON.parse(JSON.stringify(state.defaultForm)))
    },
    editForm({ rootState, state, commit }, id) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/forms/${id}`)
                .then((response) => {
                    let form = response.data.form
                    if (form) {
                        for (let i = 0; i < rootState.langs.length; i++) {
                            let lang = rootState.langs[i]
                            form[lang] = form.translations.find(
                                (translation) => translation.locale === lang
                            )
                        }

                        delete form.translations
                    }
                    commit('setNewForm', response.data.form)

                    resolve(response.data.form)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /*
    |--------------------------------------------------------------------------
    | POST/PUT form
    |--------------------------------------------------------------------------
    |
    */
    createForm({ rootState, state, commit }) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${rootState.baseURL}/forms`, createPayload(state.newForm))
                .then((response) => {
                    commit('setForms', response.data.form)
                    commit('setFormsTypes', response.data.types)
                    resolve(response.data.form)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    updateForm({ rootState, state, commit }) {
        return new Promise((resolve, reject) => {
            axios
                .put(
                    `${rootState.baseURL}/forms/${state.newForm.id}`,
                    state.newForm
                )
                .then((response) => {
                    const updatedForm = response.data.form
                    if (state.forms && Array.isArray(state.forms)) {
                        const forms = state.forms.map((f) =>
                            f.id === updatedForm.id ? updatedForm : f
                        )
                        commit('setForms', forms)
                    }
                    if (response.data.types) {
                        commit('setFormsTypes', response.data.types)
                    }
                    resolve(updatedForm)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /*
    |--------------------------------------------------------------------------
    | POST/PUT status
    |--------------------------------------------------------------------------
    |
    */
    createStatus({ rootState }, { formId, status }) {
        return new Promise((resolve, reject) => {
            axios
                .post(
                    `${rootState.baseURL}/forms/${formId}/statuses`,
                    status
                )
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    updateStatus({ rootState }, { formId, status }) {
        return new Promise((resolve, reject) => {
            axios
                .put(
                    `${rootState.baseURL}/forms/${formId}/statuses/${status.id}`,
                    status
                )
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /*
    |--------------------------------------------------------------------------
    | Status list (with predefined reasons)
    |--------------------------------------------------------------------------
    |
    | The statuses endpoint is the only one that ships `reasons.translations`:
    | the form payload (include=statuses.actions) carries the status columns but
    | not their reasons. It is also readable by an agent who may change a status
    | without holding `forms.update` — unlike the reasons CRUD below.
    */
    getFormStatuses({ rootState, commit }, formId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/forms/${formId}/statuses`)
                .then((response) => {
                    const statuses = response.data.statuses || []
                    commit('setFormStatuses', { formId, statuses })
                    resolve(statuses)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /*
    |--------------------------------------------------------------------------
    | Status predefined reasons
    |--------------------------------------------------------------------------
    |
    | Every write opens a new status version, so the versioning drawer has to be
    | refreshed after one. Permission: forms.update, reading included.
    */
    getStatusReasons({ rootState }, { formId, statusId }) {
        return new Promise((resolve, reject) => {
            axios
                .get(
                    `${rootState.baseURL}/forms/${formId}/statuses/${statusId}/reasons`
                )
                .then((response) => {
                    resolve(response.data.reasons || [])
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    createStatusReason({ rootState }, { formId, statusId, reason }) {
        return new Promise((resolve, reject) => {
            axios
                .post(
                    `${rootState.baseURL}/forms/${formId}/statuses/${statusId}/reasons`,
                    reason
                )
                .then((response) => {
                    resolve(response.data.reason)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    updateStatusReason({ rootState }, { formId, statusId, reason }) {
        return new Promise((resolve, reject) => {
            axios
                .put(
                    `${rootState.baseURL}/forms/${formId}/statuses/${statusId}/reasons/${reason.id}`,
                    reason
                )
                .then((response) => {
                    resolve(response.data.reason)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    deleteStatusReason({ rootState }, { formId, statusId, reasonId }) {
        return new Promise((resolve, reject) => {
            axios
                .delete(
                    `${rootState.baseURL}/forms/${formId}/statuses/${statusId}/reasons/${reasonId}`
                )
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /*
    |--------------------------------------------------------------------------
    | Status Versions
    |--------------------------------------------------------------------------
    |
    */
    getStatusVersions({ rootState }, { formId, statusId }) {
        return new Promise((resolve, reject) => {
            axios
                .get(
                    `${rootState.baseURL}/forms/${formId}/statuses/${statusId}/versions`
                )
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    getStatusVersion({ rootState }, { formId, statusId, versionId }) {
        return new Promise((resolve, reject) => {
            axios
                .get(
                    `${rootState.baseURL}/forms/${formId}/statuses/${statusId}/versions/${versionId}`
                )
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    restoreStatusVersion({ rootState }, { formId, statusId, versionId }) {
        return new Promise((resolve, reject) => {
            axios
                .post(
                    `${rootState.baseURL}/forms/${formId}/statuses/${statusId}/versions/${versionId}/restore`
                )
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    updateStatusAction({ rootState }, { formId, statusId, action }) {
        return new Promise((resolve, reject) => {
            axios
                .put(
                    `${rootState.baseURL}/forms/${formId}/statuses/${statusId}/actions/${action.id}`,
                    { active: action.active }
                )
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    toggleFormActive({ rootState, state, commit }, { formId, active }) {
        return new Promise((resolve, reject) => {
            axios
                .patch(`${rootState.baseURL}/forms/${formId}/toggle-active`)
                .then((response) => {
                    const forms = state.forms.map((f) =>
                        f.id === formId ? { ...f, active } : f
                    )
                    commit('setForms', forms)
                    if (state.form && state.form.id === formId) {
                        commit('setForm', { ...state.form, active })
                    }
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /*
    |--------------------------------------------------------------------------
    | DEL form
    |--------------------------------------------------------------------------
    |
    */
    deleteForm({ rootState, state, commit }, data) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`${rootState.baseURL}/forms/${data.id}`, state.newForm)
                .then((response) => {
                    let forms = state.forms
                    forms.splice(data.index, 1)

                    commit('setForms', forms)

                    resolve()
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /*
    |--------------------------------------------------------------------------
    | Form Types
    |--------------------------------------------------------------------------
    |
    */
    getFormTypes({ rootState }, axiosConfig = {}) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/form-types`, axiosConfig)
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    getFormType({ rootState }, id) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/form-types/${id}`)
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    createFormType({ rootState }, formType) {
        return new Promise((resolve, reject) => {
            axios
                .post(`${rootState.baseURL}/form-types`, formType)
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    updateFormType({ rootState }, formType) {
        return new Promise((resolve, reject) => {
            axios
                .put(
                    `${rootState.baseURL}/form-types/${formType.id}`,
                    formType
                )
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    deleteFormType({ rootState }, id) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`${rootState.baseURL}/form-types/${id}`)
                .then((response) => {
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },

    /*
    |--------------------------------------------------------------------------
    | Backoffice
    |--------------------------------------------------------------------------
    |
    */
    getBackofficeOverview({ rootState }) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/backoffice/overview`)
                .then((response) => {
                    resolve(response.data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    },
    getBackofficeFormInfo({ rootState }, formId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`${rootState.baseURL}/backoffice/forms/${formId}/info`)
                .then((response) => {
                    resolve(response.data)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }
}
